<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ------------------------------------------------------------
  // 이 컴포넌트는 Svelte 5 runes 문법을 사용합니다.
  // ============================================================

  import { router } from '@inertiajs/svelte'
  import { page } from '@inertiajs/svelte'
  import { api } from '@/utils/api'
  import { formatRelativeTime } from '@/lib/utils'
  import LikeButton from '@/components/ui/LikeButton.svelte'
  import PrivacySelector from '@/components/ui/PrivacySelector.svelte'
  import UserDisplay from '@/components/ui/UserDisplay.svelte'
  import ContentGating from '@/components/ui/ContentGating.svelte'
  import ReportModal from '@/components/ui/ReportModal.svelte'
  import CategoryBadge from '@/components/ui/CategoryBadge.svelte'
  import AdSlot from '@/components/ads/AdSlot.svelte'
  import { ARTICLE_SLOT } from '@/lib/ads'

  interface Category {
    id: number
    name: string
    color: string
  }

  interface User {
    id: number
    name: string
    nickname?: string
    organization?: string
    department?: string
    team?: string
    avatar_url?: string
  }

  interface Board {
    id: number
    name: string
    slug: string
  }

  interface Post {
    id: number
    title: string
    content: string
    privacy?: string
    // 서버가 내려주는 실제 필드. `privacy` 는 옛 이름이라 서버가 보내지 않는다.
    privacy_level?: string
    user: User
    categories?: Category[]
    views_count: number
    comments_count: number
    likes_count: number
    created_at: string
    is_owner?: boolean
    is_notice?: boolean
    full_content?: string
    is_gated?: boolean
    // 본문이 **실제로** 잘려 나갔는가. `is_gated`(게시판이 잠겼는가)와 다르다 —
    // 잠긴 게시판 글의 94%는 180자 이하라 전문이 그대로 나간다.
    content_truncated?: boolean
  }

  interface Comment {
    id: number
    content: string
    privacy?: string
    privacy_level?: string
    is_owner?: boolean
    user: User
    likes_count: number
    created_at: string
    replies?: Comment[]
  }

  let {
    board = {} as Board,
    post = {} as Post,
    comments = [] as Comment[],
    // 잠긴 게시판이면 서버가 댓글을 아예 안 싣는다 — `comments` 는 빈 배열이고
    // 이 플래그가 "없다" 와 "가려졌다" 를 가른다.
    // 🔴 빈 배열만 보고 "아직 댓글이 없습니다" 를 그리면 안 된다.
    comments_gated = false,
    // 🔴 잠긴 이유. `company_verification` 만 인증으로 열린다 —
    //    `forbidden`(admin_only · allow_view: false)에 "인증하세요" 를 붙이면
    //    인증을 마친 뒤에도 같은 벽을 만난다.
    lock_reason = null as string | null,
    liked = false,
    flash = {} as Record<string, string>
  } = $props()

  let newComment = $state('')
  let commentPrivacy = $state('full_disclosure')
  let replyingTo = $state<number | null>(null)
  let replyContent = $state('')
  let replyPrivacy = $state('full_disclosure')
  let isSubmitting = $state(false)
  let isLiked = $state(false)
  let likesCount = $state(0)
  let isTogglingLike = $state(false)
  let isVerified = $derived(Boolean(page.props.auth?.verified))
  let hasUser = $derived(Boolean(page.props.auth?.user))
  // 가려진 경우 `comments` 가 비어 있으므로 서버가 준 개수를 쓴다 —
  // 화면은 "몇 개 있는지" 까지는 말해도 된다(내용만 가린다).
  let visibleCommentsCount = $derived(
    comments_gated
      ? Number(post.comments_count || 0)
      : comments.reduce((count, comment) => count + 1 + (comment.replies?.length || 0), 0)
  )

  let showReportModal = $state(false)
  let reportTarget = $state<{ type: 'post' | 'comment', id: string, postId?: string } | null>(null)

  function openReport(type: 'post' | 'comment', id: string, postId?: string) {
    if (!isVerified) {
      window.location.href = hasUser ? '/verification' : '/register'
      return
    }
    reportTarget = { type, id, postId }
    showReportModal = true
  }

  $effect(() => {
    isLiked = Boolean(liked)
    likesCount = Number(post.likes_count || 0)
  })
  
  async function toggleLike() {
    if (!isVerified) {
      window.location.href = hasUser ? '/verification' : '/register'
      return
    }

    if (isTogglingLike) return

    isTogglingLike = true

    const response = await api.post<{ liked: boolean; likesCount: number }>(`/likes/Post/${post.id}/toggle`)

    if (response.success && response.data) {
      isLiked = Boolean(response.data.liked)
      likesCount = Number(response.data.likesCount || 0)
    } else {
      alert(response.error || '좋아요 처리 중 오류가 발생했습니다.')
    }

    isTogglingLike = false
  }
  
  function submitComment(e: Event) {
    e.preventDefault()

    isSubmitting = true
    router.post(`/boards/${board.slug}/posts/${post.id}/comments`, {
      comment: { content: newComment, privacy_level: commentPrivacy }
    }, {
      preserveState: false,
      onFinish: () => {
        isSubmitting = false
        newComment = ''
        commentPrivacy = 'full_disclosure'
      }
    })
  }

  function submitReply(parentId: number) {

    isSubmitting = true
    router.post(`/boards/${board.slug}/posts/${post.id}/comments`, {
      comment: { content: replyContent, parent_id: parentId, privacy_level: replyPrivacy }
    }, {
      preserveState: false,
      onFinish: () => {
        isSubmitting = false
        replyContent = ''
        replyPrivacy = 'full_disclosure'
        replyingTo = null
      }
    })
  }
  
  function deletePost() {
    if (confirm('정말 삭제하시겠습니까?')) {
      router.delete(`/boards/${board.slug}/posts/${post.id}`)
    }
  }
</script>

<div class="space-y-6">
  <!-- Breadcrumb -->
  <nav class="flex items-center gap-1.5 text-caption text-content-tertiary pb-2">
    <a href="/boards" class="inline-flex items-center gap-1 hover:text-primary transition-colors cursor-pointer">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
      </svg>
      게시판
    </a>
    <svg class="w-3.5 h-3.5 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <a href={`/boards/${board.slug}`} class="hover:text-primary transition-colors cursor-pointer font-medium">{board.name}</a>
    <svg class="w-3.5 h-3.5 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <span class="text-content font-medium truncate max-w-xs">{post.title}</span>
  </nav>

  <main>
    <!-- Flash Messages -->
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    
    <!-- Post -->
    <article class="card card-bordered overflow-hidden mb-8">
      <!-- Post Header -->
      <div class="p-6 lg:p-8 border-b border-default">
        <div class="flex items-center gap-2 mb-4">
          {#if post.is_notice}
            <span class="badge badge-error">공지</span>
          {/if}
          {#each post.categories || [] as category}
            <CategoryBadge {category} size="md" />
          {/each}
        </div>
        
        <h1 class="text-h1 text-content mb-4">{post.title}</h1>
        
        <div class="flex items-center justify-between">
          <UserDisplay
            user={post.user}
            privacy={post.privacy_level || post.privacy || 'full_disclosure'}
            size="lg"
            showAvatar={true}
          />

          <div class="flex items-center gap-4 text-caption text-content-tertiary">
            <span>{formatRelativeTime(post.created_at)}</span>
            <span class="flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
              {post.views_count || 0}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Post Content -->
      <div class="p-6 lg:p-8">
        <ContentGating
          ctaText={hasUser ? '소속 인증 후 전체 본문 보기' : '1분 만에 가입하고 전체 본문 보기'}
          ctaHref={hasUser ? '/verification' : '/register'}
        >
          {#snippet preview()}
            <div class="prose prose-base lg:prose-lg prose-gray dark:prose-invert max-w-none prose-p:leading-7 prose-li:leading-7 prose-headings:scroll-mt-24 text-gray-700 dark:text-dark-text-medium">
              {@html post.content}
            </div>
          {/snippet}
          {#snippet full()}
            <div class="prose prose-base lg:prose-lg prose-gray dark:prose-invert max-w-none prose-p:leading-7 prose-li:leading-7 prose-headings:scroll-mt-24 text-gray-700 dark:text-dark-text-medium">
              {@html post.full_content || post.content}
            </div>
          {/snippet}
        </ContentGating>
      </div>
      
      <!-- Post Actions -->
      <div class="p-6 lg:p-8 border-t border-default bg-surface-tertiary">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <LikeButton
              liked={isLiked}
              count={likesCount}
              disabled={isTogglingLike}
              ontoggle={toggleLike}
            />
            
            <span class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-dark-secondary text-content-tertiary">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              <span class="text-sm font-medium">댓글 {post.comments_count || 0}</span>
            </span>
          </div>
          
          {#if post.is_owner}
            <div class="flex items-center gap-2">
              <a
                href={`/boards/${board.slug}/posts/${post.id}/edit`}
                class="btn btn-secondary btn-sm"
              >
                수정
              </a>
              <button
                onclick={deletePost}
                class="btn btn-danger btn-sm"
              >
                삭제
              </button>
            </div>
          {:else if hasUser}
            <button
              onclick={() => openReport('post', String(post.id))}
              class="flex items-center gap-1.5 px-3 py-1.5 text-caption text-content-tertiary hover:text-error hover:bg-error/10 rounded-lg transition-colors"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              신고
            </button>
          {/if}
        </div>
      </div>
    </article>

    <!-- 본문·댓글 사이 디스플레이 광고. 잠긴 글은 본문 끝·댓글 영역 두 곳에
         인증 CTA 가 있어 어디에 두어도 CTA 에 붙는다(오클릭) — 싣지 않는다. -->
    {#if !comments_gated && !post.content_truncated}
      <AdSlot variant="article" slot={ARTICLE_SLOT} format="auto" />
    {/if}

    <!-- Comments -->
    <section>
      <h2 class="text-h2 text-content mb-4">댓글 {visibleCommentsCount}개</h2>
      
      <!-- Comment Form -->
      {#if comments_gated}
        <!-- 잠긴 게시판 — 서버가 댓글을 안 보냈다. 작성 폼도 열지 않는다. -->
      {:else if isVerified}
        <form onsubmit={submitComment} class="mb-6 card p-5">
          <textarea
            bind:value={newComment}
            placeholder="댓글을 작성해주세요..."
            rows="3"
            class="input resize-none mb-4"
          ></textarea>

          <div class="flex items-center justify-between">
            <!-- Privacy Selector (Compact) -->
            <div class="flex-1">
              <PrivacySelector
                value={commentPrivacy}
                onChange={(level: string) => commentPrivacy = level}
                size="sm"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              class="btn btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed ml-3"
            >
              {isSubmitting ? '등록 중...' : '댓글 등록'}
            </button>
          </div>
        </form>
      {:else}
        <div class="mb-6 card p-5 border border-primary/20 bg-primary-50 dark:bg-primary/10">
          <p class="text-body2 text-gray-700 dark:text-dark-text-medium mb-3">
            댓글 작성은 인증 회원만 가능합니다.
          </p>
          <a class="btn btn-primary btn-sm" href={hasUser ? '/verification' : '/register'}>
            {hasUser ? '인증하고 댓글 쓰기' : '가입하고 댓글 쓰기'}
          </a>
        </div>
      {/if}
      
      <!-- Comment List -->
      {#if comments_gated && lock_reason === 'forbidden'}
        <!-- 인증해도 안 열리는 게시판 — CTA 를 주지 않는다. -->
        <div class="card p-6 border border-default text-center">
          <p class="text-body2 text-content">이 게시판의 댓글은 볼 수 없어요.</p>
          <p class="text-caption text-content-tertiary mt-1">접근 권한이 필요한 게시판입니다.</p>
        </div>
      {:else if comments_gated}
        <div class="card p-6 border border-default text-center">
          <p class="text-body2 text-content mb-1">
            댓글은 소속 인증 후 볼 수 있어요.
          </p>
          <p class="text-caption text-content-tertiary mb-4">
            인증을 마치면 이 글 전체와<br />댓글 {visibleCommentsCount}개를 모두 읽을 수 있습니다.
          </p>
          <a class="btn btn-primary btn-sm" href={hasUser ? '/verification' : '/register'}>
            {hasUser ? '소속 인증하기' : '가입하고 인증하기'}
          </a>
        </div>
      {:else}
      <div class="space-y-4">
        {#each comments as comment (comment.id)}
          <div class="card p-5 border border-default">
            <div class="flex items-start gap-3">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <UserDisplay
                    user={comment.user}
                    privacy={comment.privacy_level || comment.privacy || 'full_disclosure'}
                    size="sm"
                    showAvatar={true}
                  />
                  <span class="text-caption text-content-tertiary">{formatRelativeTime(comment.created_at)}</span>
                </div>
                <p class="text-body2 text-gray-700 dark:text-dark-text-medium mb-3">{comment.content}</p>

                <div class="flex items-center gap-4 mt-3">
                  <button class="inline-flex items-center gap-1.5 text-caption text-content-tertiary hover:text-error transition-colors font-medium">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                    {comment.likes_count || 0}
                  </button>
                  <button
                    onclick={() => replyingTo = replyingTo === comment.id ? null : comment.id}
                    class="text-caption text-content-tertiary hover:text-primary transition-colors font-medium"
                  >
                    답글
                  </button>
                  {#if !comment.is_owner && hasUser}
                    <button
                      onclick={() => openReport('comment', String(comment.id), String(post.id))}
                      class="text-caption text-gray-400 dark:text-dark-text-disabled hover:text-error transition-colors"
                    >
                      신고
                    </button>
                  {/if}
                </div>
                
                <!-- Reply Form -->
                {#if replyingTo === comment.id}
                  <div class="mt-4 pt-4 border-t border-gray-100 dark:border-dark-border">
                    <textarea
                      bind:value={replyContent}
                      placeholder="답글을 작성해주세요..."
                      rows="2"
                      class="input resize-none text-body2 mb-3"
                    ></textarea>
                    <div class="flex justify-end gap-2">
                      <button
                        onclick={() => replyingTo = null}
                        class="btn btn-secondary btn-sm"
                      >
                        취소
                      </button>
                      <button
                        onclick={() => submitReply(comment.id)}
                        disabled={isSubmitting}
                        class="btn btn-primary btn-sm disabled:opacity-50"
                      >
                        등록
                      </button>
                    </div>
                  </div>
                {/if}
                
                <!-- Replies -->
                {#if (comment.replies?.length ?? 0) > 0}
                  <div class="mt-4 space-y-3 pl-4 border-l-2 border-default">
                    {#each (comment.replies || []) as reply (reply.id)}
                      <div class="flex items-start gap-2">
                        <div class="flex-1">
                          <div class="flex items-center gap-2 mb-1">
                            <UserDisplay
                              user={reply.user}
                              privacy={reply.privacy_level || reply.privacy || 'full_disclosure'}
                              size="sm"
                              showAvatar={true}
                            />
                            <span class="text-caption text-content-tertiary">{formatRelativeTime(reply.created_at)}</span>
                          </div>
                          <p class="text-caption text-gray-700 dark:text-dark-text-medium whitespace-pre-wrap">{reply.content}</p>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/each}
        
        {#if comments.length === 0}
          <div class="text-center py-12">
            <svg class="w-12 h-12 text-gray-300 dark:text-dark-text-disabled mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            <p class="text-body2 text-content-tertiary">아직 댓글이 없습니다</p>
            <p class="text-caption text-gray-400 dark:text-dark-text-disabled mt-1">첫 댓글을 남겨보세요!</p>
          </div>
        {/if}
      </div>
      {/if}
    </section>

    <!-- Navigation -->
    <div class="flex justify-center mt-8">
      <a
        href={`/boards/${board.slug}`}
        class="btn btn-secondary btn-md"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        목록으로
      </a>
    </div>
  </main>
</div>

{#if showReportModal && reportTarget}
  <ReportModal
    isOpen={showReportModal}
    targetType={reportTarget.type}
    targetId={reportTarget.id}
    postId={reportTarget.postId}
    onClose={() => { showReportModal = false; reportTarget = null }}
  />
{/if}
